import { DropDown } from '@components/core-ui/form-elements/DropDown';
import Dots from '@components/icons/Dots.svg';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { formatDateMsDsY } from '@helpers/formatDateMsDsY';
import { formatRate } from '@helpers/formatRate';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { getHourPrice } from '@helpers/getHourPrice';
import { isSubscriptionEditable } from '@helpers/isSubscriptionEditable';
import type { PlanSelection } from '@pages/User/BillingAccountDetailsPage/components/SubscriptionsWrapper/AddSubscription';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { routes } from '@routes';
import {
  GetAllSubscriptionPlansPlanResponseDto,
  GetBillingAccountResponseDto,
  GetBillingAccountResponseSubscriptionDto,
  PrismaSubscriptionProductDto,
} from 'app/API';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DotsButton } from './styles/DotsButton';
import { TextButtonLarge } from '@components/core-ui/form-elements/buttons/Button/variations/TextButtonLarge';
import { Wrapper } from './styles/Wrapper';

type TProps = {
  className?: string;
  reload: () => void;
  plan: GetAllSubscriptionPlansPlanResponseDto;
  availablePlans: PrismaSubscriptionProductDto[];
  subscription: GetBillingAccountResponseSubscriptionDto;
  billingAccount: GetBillingAccountResponseDto;
};

export const ActiveCard = ({ className = '', plan, subscription, billingAccount, reload, availablePlans }: TProps) => {
  const { openModal, closeModal } = useModalContext();
  const navigate = useNavigate();
  const { organization, updateMe } = useAuthContext();
  // const licensesCount = subscription?.licenses?.length || 1;

  const [showDropDown, setShowDropDown] = useState(false);

  const planName = plan?.name.replace('AutoScriptOne ', '').replace('RealTime ', '');
  const price = getHourPrice(Number(plan?.price), true);
  const billingConfiguration = plan.DefaultBillingConfiguration;

  const unassignedCount = subscription?.licenses.filter((lic) => lic.assigneeId === null).length;
  const assignedCount = subscription?.licenses.filter((lic) => lic.assigneeId !== null).length;

  const showUpgradeButton = availablePlans.find((p) => Number(p.price) > Number(plan.price));

  if (!billingConfiguration) {
    return null;
  }

  const onEditClick = (e: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    openModal(ModalNamesEnum.EditASOneSubscriptionModal, {
      subscription,
      onSuccess: () => {
        closeModal();
        if (reload) {
          reload();
        }
      },
    });
  };

  const onUpgradeClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    openModal(ModalNamesEnum.ChooseASOneSubscriptionModal, {
      billingAccountId: billingAccount?.id,
      filterPlans: availablePlans.filter((p) => Number(p.price ?? 0) < Number(plan.price ?? 0)).map((p) => p.id),
      onSuccess: ({ subscriptionProductId, planName, price, freeSeats }: PlanSelection) => {
        closeModal();
        openModal(ModalNamesEnum.ConfirmSubscriptionUpgrade, {
          billingAccountId: billingAccount?.id,
          subscriptionId: subscription.id,
          subscriptionProductId,
          planName,
          freeSeats,
          price: Number(price),
          licenses: subscription.licenses.length,
          onSuccess: () => {
            closeModal();
            reload();
          },
        });
      },
    });
  };

  const dropDownOptions: {
    title: string;
    onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
    testId?: string;
  }[] = [];

  if (organization?.permissions?.viewLicenses) {
    dropDownOptions.push({
      title: 'Details',
      onClick: (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        e.preventDefault();
        navigate(routes.autoScriptOneSubscriptionDetails.make(organization?.slug ?? '', subscription.id));
      },
      testId: 'subscription-details-link',
    });
  }

  if (organization?.permissions?.manageLicenses && isSubscriptionEditable(subscription.status)) {
    dropDownOptions.push({
      title: 'Edit Subscription',
      onClick: (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        e.preventDefault();
        openModal(ModalNamesEnum.EditASOneSubscriptionModal, {
          subscription,
          onSuccess: () => {
            closeModal();
            reload();
          },
        });
      },
    });
  }

  if (organization?.permissions?.manageLicenses) {
    dropDownOptions.push({
      title: 'Cancel',
      onClick: (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        e.preventDefault();
        openModal(ModalNamesEnum.ConfirmSubscriptionCancellation, {
          billingAccountId: billingAccount?.id,
          subscriptionId: subscription.id,
          planName: plan.name,
          onSuccess: () => {
            closeModal();
            updateMe();
            reload();
          },
        });
      },
    });
  }

  const renderOptions = () => {
    return (
      <>
        <DotsButton
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setShowDropDown(true);
          }}
          data-testid="subscription-dropdown"
        >
          <Dots />
        </DotsButton>
        {showDropDown ? <DropDown close={() => setShowDropDown(false)} options={dropDownOptions} /> : null}
      </>
    );
  };

  return (
    <Wrapper className={className} data-testid="active-subscription-card">
      <div className="content">
        <h2 className="title">AutoScriptOne</h2>
        <h2 className="plan-name">{planName}</h2>
        {dropDownOptions.length > 0 && renderOptions()}

        <div className="price flex gap-3 pt-1 pb-2">
          <div>
            <div>{subscription.cancelAt ? 'Daily' : 'Monthly'}</div>
            <div>{price}/seat</div>
          </div>
          {/* biome-ignore lint/style/useSelfClosingElements: Required for consistent styling with existing dividers */}
          <div className="divider"></div>
          <div>
            <div>Active</div>
            <div>
              {assignedCount}/{assignedCount + unassignedCount}
            </div>
          </div>
        </div>

        {billingConfiguration.postProcessingRateType !== 'DISALLOWED' &&
        billingConfiguration.postProcessingRateType !== 'TIER_RATE' ? (
          <h3>
            {formatRate(
              billingConfiguration.postProcessingRate,
              billingConfiguration.postProcessingRateType,
              'Post ASR',
              false,
            )}
          </h3>
        ) : null}
        {billingConfiguration.realTimeRateType !== 'DISALLOWED' ? (
          <h3>
            {formatRate(
              billingConfiguration.realTimeRate,
              billingConfiguration.realTimeRateType,
              'Real-time streaming',
              false,
            )}
          </h3>
        ) : null}
        {billingConfiguration.partialUnlockRateType !== 'DISALLOWED' ? (
          <h3>
            {formatRate(
              billingConfiguration.partialUnlockRate,
              billingConfiguration.partialUnlockRateType,
              'Reporter unlock',
              false,
            )}
          </h3>
        ) : null}
        {billingConfiguration.fullUnlockRateType !== 'DISALLOWED' ? (
          <h3>
            {formatRate(
              billingConfiguration.fullUnlockRate,
              billingConfiguration.fullUnlockRateType,
              'Full unlock',
              false,
            )}
          </h3>
        ) : null}
        {subscription.currentPeriodEnd ? <h3>Next payment: {formatDateMsDsY(subscription.currentPeriodEnd)}</h3> : null}
        {subscription.freeTrialEndDate ? (
          <h3>Trial expiration: {formatDateMsDsY(subscription.freeTrialEndDate)}</h3>
        ) : null}

        {subscription.cancelAt ? (
          <h3>
            Subscription expiration: <p>{getAmericanTime(subscription.cancelAt)}</p>
          </h3>
        ) : null}
      </div>

      {!showUpgradeButton &&
      organization?.permissions?.manageLicenses &&
      isSubscriptionEditable(subscription.status) ? (
        <div className="buttonContainer">
          <TextButtonLarge onClick={onEditClick}>Edit Subscription</TextButtonLarge>
        </div>
      ) : null}

      {showUpgradeButton && organization?.permissions?.manageLicenses && isSubscriptionEditable(subscription.status) ? (
        <div className="buttonContainer">
          <TextButtonLarge onClick={onUpgradeClick}>Upgrade</TextButtonLarge>
        </div>
      ) : null}
    </Wrapper>
  );
};
